'use client';

import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { Upload, Download, Copy, Trash2, FileJson, ArrowRight, ArrowDown, Check } from 'lucide-react';
import { saveAs } from 'file-saver';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { jsonPrettifyContent } from '../../data/json-prettify-content';
import Link from 'next/link';

interface CodeEditorProps {
    value: string;
    onChange?: (value: string) => void;
    readOnly?: boolean;
    placeholder?: string;
    className?: string;
}

const CodeEditor = ({ value, onChange, readOnly, placeholder, className }: CodeEditorProps) => {
    return (
        <div className={cn(
            "relative flex h-full w-full overflow-hidden rounded-md border bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
            className
        )}>
            <Textarea
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                readOnly={readOnly}
                className="h-full w-full resize-none border-0 focus-visible:ring-0 rounded-none p-4 font-mono text-sm leading-6"
                placeholder={placeholder}
                spellCheck={false}
            />
        </div>
    );
};

export default function JsonPrettifier() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [indent, setIndent] = useState<number | 'tabs'>(2);
    const [stats, setStats] = useState<{ size: string; items: number } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { toast } = useToast();

    const calculateStats = (jsonStr: string, parsedData: any) => {
        const bytes = new Blob([jsonStr]).size;
        const size = bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(2)} KB`;

        let items = 0;
        if (Array.isArray(parsedData)) {
            items = parsedData.length;
        } else if (typeof parsedData === 'object' && parsedData !== null) {
            items = Object.keys(parsedData).length;
        }

        setStats({ size, items });
    };

    const formatJson = () => {
        try {
            if (!input.trim()) {
                toast({
                    title: "Error",
                    description: "Please enter some JSON to format",
                    variant: "destructive",
                });
                return;
            }

            const parsed = JSON.parse(input);
            const spaces = indent === 'tabs' ? '\t' : indent;
            const formatted = JSON.stringify(parsed, null, spaces);
            setOutput(formatted);
            calculateStats(formatted, parsed);

            toast({
                title: "Success",
                description: "JSON formatted successfully!",
            });
        } catch (error) {
            let errorMessage = "Invalid JSON. Please check your input.";
            if (error instanceof Error) {
                errorMessage = error.message;
            }

            toast({
                title: "Invalid JSON",
                description: errorMessage,
                variant: "destructive",
            });
            console.error('Error formatting JSON:', error);
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
            toast({
                title: "File Uploaded",
                description: `Loaded ${file.name}`,
            });
        };
        reader.readAsText(file);
    };

    const handleDownload = () => {
        if (!output) return;
        const blob = new Blob([output], { type: "application/json;charset=utf-8" });
        saveAs(blob, "formatted.json");
        toast({
            title: "Downloaded",
            description: "File saved as formatted.json",
        });
    };

    const copyToClipboard = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        toast({
            title: "Copied!",
            description: "Formatted JSON copied to clipboard",
        });
    };

    const clearAll = () => {
        setInput('');
        setOutput('');
        setStats(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <div className="min-h-screen bg-[#F9FAFB] py-10 text-slate-900 dark:bg-slate-950 dark:text-slate-50">
            <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
                <div className="mb-8 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
                    <ToolPageIntro title="JSON Prettifier">
                        Format and beautify JSON with consistent indentation. Upload a file or paste; choose indent size and get readable output with optional stats. Handy for config files, API responses, and debugging.
                    </ToolPageIntro>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            className="rounded-full border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500 dark:hover:bg-slate-900"
                        >
                            <Upload className="mr-2 h-4 w-4" /> Upload JSON
                        </Button>
                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept=".json,application/json"
                            onChange={handleFileUpload}
                        />
                    </div>
                </div>

                {/* Main card */}
                <div className="mb-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    {/* Toolbar */}
                    <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/80">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="text-xs text-slate-600 dark:text-slate-400">
                                <p className="font-semibold uppercase tracking-[0.16em]">
                                    Format your JSON
                                </p>
                                <p>Paste or upload JSON, then prettify with one click.</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={clearAll}
                                    className="rounded-full border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                                >
                                    <Trash2 className="mr-1 h-3.5 w-3.5" /> Clear all
                                </Button>
                                <Button
                                    onClick={formatJson}
                                    size="sm"
                                    className="inline-flex items-center rounded-full bg-[#4F46E5] px-4 py-1.5 text-xs font-semibold text-white shadow-sm shadow-indigo-500/30 hover:bg-[#4338CA] hover:shadow-md"
                                >
                                    Format JSON
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="grid border-t border-slate-100 lg:grid-cols-2 dark:border-slate-800">
                        {/* Input Section */}
                        <div className="flex min-h-[320px] flex-col border-b border-slate-100 bg-white/60 p-4 dark:border-slate-800 dark:bg-slate-950/60 lg:border-b-0 lg:border-r">
                            <div className="mb-2 flex items-center justify-between gap-2">
                                <span className="text-xs font-medium text-slate-700 dark:text-slate-200">
                                    Input JSON
                                </span>
                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                                    Client-side · Private
                                </span>
                            </div>
                            <div className="relative flex-1">
                                <CodeEditor
                                    value={input}
                                    onChange={setInput}
                                    placeholder="Paste your JSON here or upload a file..."
                                    className="border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900"
                                />
                            </div>
                        </div>

                        {/* Output Section */}
                        <div className="flex min-h-[320px] flex-col bg-slate-50/80 p-4 dark:bg-slate-950/60">
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                <span className="text-xs font-medium text-slate-700 dark:text-slate-200">
                                    Prettified output
                                </span>
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                                        <span>Indent</span>
                                        <select
                                            value={indent}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setIndent(val === 'tabs' ? 'tabs' : parseInt(val));
                                            }}
                                            className="bg-transparent text-xs focus:outline-none"
                                        >
                                            <option value={2}>2 spaces</option>
                                            <option value={4}>4 spaces</option>
                                            <option value="tabs">Tabs</option>
                                            <option value={0}>Minify</option>
                                        </select>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={copyToClipboard}
                                        disabled={!output}
                                        title="Copy"
                                        className="h-8 w-8 rounded-full border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-800"
                                    >
                                        <Copy className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={handleDownload}
                                        disabled={!output}
                                        title="Download"
                                        className="h-8 w-8 rounded-full border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-800"
                                    >
                                        <Download className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                            <div className="relative flex-1">
                                <CodeEditor
                                    value={output}
                                    readOnly
                                    placeholder="Formatted JSON will appear here..."
                                    className="border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
                                />
                                {stats && (
                                    <div className="pointer-events-auto absolute inset-x-2 bottom-2 flex gap-4 rounded-xl border border-slate-200 bg-white/90 px-3 py-1.5 text-[11px] text-slate-600 backdrop-blur dark:border-slate-700 dark:bg-slate-900/90 dark:text-slate-300">
                                        <span>
                                            Size:{' '}
                                            <span className="font-semibold text-slate-800 dark:text-slate-100">
                                                {stats.size}
                                            </span>
                                        </span>
                                        <span>
                                            Items:{' '}
                                            <span className="font-semibold text-slate-800 dark:text-slate-100">
                                                {stats.items}
                                            </span>
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Informational Content */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <InformationalContent
                        title={jsonPrettifyContent.title}
                        description={jsonPrettifyContent.description}
                        content={jsonPrettifyContent.content}
                        faqs={jsonPrettifyContent.faqs}
                        relatedTools={jsonPrettifyContent.relatedTools}
                    />
                </div>

                <Toaster />
            </div>
        </div>
    );
}

